import { useInfiniteQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'

import { NO_MORE_DATA, TEMP_USER_ID } from 'constant'
import { fetchSpotsFeed } from 'server-actions/fetch-spots'
import { useSpotLocationPickerStore } from 'store/global/location-picker'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import type { IInStreamSpotReqV2, ISpotLocationPickerV2 } from 'type/feed'
import convertLevelType from 'util/convertLevelType'

const useSpotFeed = (pickedLocation: ISpotLocationPickerV2 | null) => {
  const userSession = useSessionStore(state => state.session)
  const locale = useLocale()
  const isRefresh = useSpotLocationPickerStore(state => state.isRefresh)
  const refreshingId = useSpotLocationPickerStore(state => state.refreshingId)
  const ipLocation = useIpLocationStore(state => state.ipLocation)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading
  } = useInfiniteQuery({
    queryKey: ['spotInStream', refreshingId],
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const { lat, lng } = ipLocation?.ip_geo_point || { lat: 0, lng: 0 }

      const userId = userSession.guest ? TEMP_USER_ID : userSession.user_id
      let locationId = ''
      if (pickedLocation) {
        if (pickedLocation.place) {
          locationId = pickedLocation.place.location.id || ''
        } else if (pickedLocation.location) {
          locationId = pickedLocation.location.id
        }
      }

      const req: IInStreamSpotReqV2 = {
        info: {
          country: ipLocation?.country_code || '',
          language: locale,
          user_id: userId,
          user_coordinate: {
            lat: `${lat}`,
            lng: `${lng}`
          }
        },
        location_id: locationId,
        location_picker: pickedLocation
          ? {
              pick_content_ids: pickedLocation.content_ids || [],
              level_type: convertLevelType(pickedLocation.level_type)
            }
          : null,
        session_id: pageParam,
        is_refresh: isRefresh,
        is_change_location: !!pickedLocation
      }

      const res = fetchSpotsFeed(req)

      useSpotLocationPickerStore.setState({
        isRefresh: false,
        pickedLocation: null
      })

      return res
    },
    select: data => data.pages.flatMap(page => page.spots),
    getNextPageParam: lastPage => {
      return lastPage.session?.id || NO_MORE_DATA
    }
  })

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading
  }
}

export default useSpotFeed
